/**
 * Typed tenant user management API facade over the generated contract client.
 */

import * as dt from '../../../shared/src/data.types';
import { tenantUsersApi } from '../api/generated/tenantUsers.api.js';
import { apiClient } from '../api/ApiClient.js';
import {
  type CreateTenantUserRequest,
  type CreateUserResponseData,
  type GetTenantUsersRequest,
  type GetUserByIdResponseData,
  type GetUsersResponseData,
  type UpdateTenantUserRequest,
  type UserResponse,
} from '@cnapse/shared/schemas.js';

export type TenantUserDetail = UserResponse;
export type GetUsersRequest = GetTenantUsersRequest;

class TenantUserService {
  async getTenantUsersList(params?: GetUsersRequest): Promise<GetUsersResponseData> {
    return tenantUsersApi.list(params ?? {}) as Promise<GetUsersResponseData>;
  }

  async getTenantUserById(userId: string): Promise<GetUserByIdResponseData> {
    return tenantUsersApi.getById(userId) as Promise<GetUserByIdResponseData>;
  }

  async createNewTenantUser(userData: CreateTenantUserRequest): Promise<CreateUserResponseData> {
    return tenantUsersApi.create(userData) as Promise<CreateUserResponseData>;
  }

  async updateTenantUser(
    userId: dt.IntegerId,
    updates: UpdateTenantUserRequest,
    stepUpToken?: string,
  ): Promise<CreateUserResponseData> {
    if (stepUpToken) {
      return apiClient.withStepUpToken(stepUpToken, () =>
        tenantUsersApi.update(userId, updates),
      ) as Promise<CreateUserResponseData>;
    }
    return tenantUsersApi.update(userId, updates) as Promise<CreateUserResponseData>;
  }

  async deleteTenantUser(userId: dt.IntegerId, stepUpToken: string): Promise<void> {
    await apiClient.withStepUpToken(stepUpToken, () => tenantUsersApi.delete(userId));
  }

  async resendTenantUserInvitation(userId: dt.IntegerId, stepUpToken: string): Promise<void> {
    await apiClient.withStepUpToken(stepUpToken, () => tenantUsersApi.resendInvitation(userId));
  }

  async resetTenantUserPassword(userId: dt.IntegerId, stepUpToken: string): Promise<void> {
    await apiClient.withStepUpToken(stepUpToken, () => tenantUsersApi.resetPassword(userId));
  }
}

export const tenantUserService = new TenantUserService();
